import { AppIcon } from '@/components/ui/app-icon';
import { iconArrowDown, iconPlus, iconSpinnerThird } from '@/lib/icons';
import { cn } from '@/lib/utils';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Link } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useMemo } from 'react';
import { ChatTabIcon } from './chat-tab-icon.component';
import { ChatTitleField } from './chat-title-field.component';
import { chatRowKey, type ChatTab } from './chat-tabs.util';
import { PinIcon } from './pin-icon.component';
import { dragId } from './sidebar-dnd.util';
import { focusFirst } from './sidebar-focus.util';
import { SidebarItemMenu } from './sidebar-item-menu.component';
import type { SidebarMenuPlace } from './sidebar-menu.util';
import { PinTarget } from './sidebar-nav.component';
import {
  itemLabel,
  RAISED_CARD,
  SIDEBAR_HOVER,
  SIDEBAR_ICON,
  SIDEBAR_RULE,
} from './tabs-sidebar.styles';
import type { ChatTabsState } from './use-chat-tabs.hook';
import { useRenameInPlace } from './use-rename-in-place.hook';
import { useSortablePin } from './use-sortable-pin.hook';
import type { TabsNavItem } from './use-tabs-nav.hook';

const CHAT_TAB: SidebarMenuPlace = { kind: 'chat-tab' };
const PAGE_TAB: SidebarMenuPlace = { kind: 'page-tab' };

/** A chat's tab as the item a drag carries into the pins. */
const chatItem = (tab: ChatTab): TabsNavItem => ({
  key: `chat:${tab.id}`,
  ref: { kind: 'chat', id: tab.id },
  label: tab.title,
  iconName: 'comment',
  chat: tab,
});

const FOCUS_RING =
  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50';

const tabClasses = (active: boolean) =>
  cn(
    'flex h-9 w-full items-center gap-2 rounded-[10px] pr-3 pl-2 text-left text-foreground transition-colors group-focus-within/pin:pr-8 group-hover/pin:pr-8',
    FOCUS_RING,
    active ? RAISED_CARD : SIDEBAR_HOVER
  );

/**
 * The tabs, under a rule with Clear: New chat first where Clu is available,
 * as Arc's New Tab, then the page on screen when nothing pinned covers it,
 * then the user's chats. The tab for what is on screen is the white raised
 * card.
 */
export function ChatTabs({
  state,
  pageTab = null,
  pageTabActive = true,
}: {
  state: ChatTabsState;
  pageTab?: TabsNavItem | null;
  /** False while the Clu dock shows a chat beside the page. */
  pageTabActive?: boolean;
}) {
  const {
    available,
    tabs,
    activeId,
    runningIds,
    clear,
    openCluChat,
    newChat,
    rename,
  } = state;
  const posthog = usePostHog();
  const chats = useMemo(() => (available ? tabs : []), [available, tabs]);
  // Tabs are dragged out to pin them; they take no drops of their own. A
  // chat still waiting for its id has nothing to pin yet.
  const ids = useMemo(
    () => [
      ...(pageTab ? [dragId('tabs', pageTab.key)] : []),
      ...chats
        .filter((tab) => !tab.pending)
        .map((tab) => dragId('tabs', `chat:${tab.id}`)),
    ],
    [chats, pageTab]
  );
  return (
    <SortableContext
      id='tabs'
      items={ids}
      strategy={verticalListSortingStrategy}
    >
      <section aria-label='Chats' className='mt-1'>
        <div className='flex h-5 items-center gap-1.5 pl-2'>
          <span aria-hidden className={cn('h-px flex-1', SIDEBAR_RULE)} />
          {available && (
            <button
              type='button'
              onClick={() => {
                posthog?.capture('sidebar_chats_cleared', {
                  count: chats.length,
                });
                clear();
              }}
              disabled={chats.length === 0}
              className={cn(
                'flex h-5 items-center gap-1 rounded-md px-1.5 text-xs transition-colors disabled:pointer-events-none disabled:opacity-40',
                SIDEBAR_ICON,
                SIDEBAR_HOVER
              )}
            >
              <AppIcon icon={iconArrowDown} style={{ fontSize: 11 }} />
              Clear
            </button>
          )}
        </div>
        {(available || pageTab) && (
          <ul className='mt-0.5 flex flex-col gap-0.5'>
            {available && (
              <li>
                <NewChatRow
                  onClick={() => {
                    posthog?.capture('sidebar_new_chat_clicked');
                    newChat();
                  }}
                />
              </li>
            )}
            {pageTab && (
              <li>
                <PageTabItem
                  item={pageTab}
                  active={pageTabActive}
                  onOpenClu={openCluChat}
                />
              </li>
            )}
            {chats.map((tab) => (
              <li key={chatRowKey(tab)}>
                <ChatTabItem
                  tab={tab}
                  active={tab.id === activeId}
                  running={runningIds.has(tab.id)}
                  onOpenClu={openCluChat}
                  onRename={rename}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </SortableContext>
  );
}

/** Starts a new Clu chat. Not a tab: it can't be dragged or pinned. */
function NewChatRow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-[10px] pr-3 pl-2 text-left transition-colors',
        FOCUS_RING,
        SIDEBAR_ICON,
        SIDEBAR_HOVER
      )}
    >
      <span className='grid w-5 shrink-0 place-items-center'>
        <AppIcon icon={iconPlus} style={{ fontSize: 15 }} />
      </span>
      <span className='min-w-0 flex-1 truncate'>New chat</span>
    </button>
  );
}

/**
 * The page on screen, unpinned, or the agent whose workspace it is: a tab
 * with Pin, the active one unless the Clu dock shows a chat beside it.
 */
function PageTabItem({
  item,
  active,
  onOpenClu,
}: {
  item: TabsNavItem;
  active: boolean;
  onOpenClu: (id: string) => void;
}) {
  const { nodeRef, handleProps, isDragging } = useSortablePin(
    'tabs',
    item,
    'row'
  );
  return (
    <SidebarItemMenu
      ref={nodeRef}
      sidebarRef={item.ref}
      label={item.label}
      place={PAGE_TAB}
      button
      className={cn('grid', isDragging && 'opacity-40')}
    >
      <PinTarget
        item={item}
        placement='tab'
        onOpenClu={onOpenClu}
        {...handleProps}
        data-pin-key={item.key}
        aria-current={active ? 'page' : undefined}
        className={tabClasses(active)}
      >
        <span className='grid w-5 shrink-0 place-items-center'>
          <PinIcon item={item} size={16} filled={active} />
        </span>
        <span className={cn('min-w-0 flex-1 truncate', itemLabel(active))}>
          {item.label}
        </span>
      </PinTarget>
    </SidebarItemMenu>
  );
}

/**
 * A chat's tab. One still waiting for its id is the same row with nothing
 * to drag and no menu: it opens where the runtime shows it. A tab is renamed
 * in place, its title a field that nothing drags.
 */
function ChatTabItem({
  tab,
  active,
  running,
  onOpenClu,
  onRename,
}: {
  tab: ChatTab;
  active: boolean;
  running: boolean;
  onOpenClu: (id: string) => void;
  onRename?: (id: string, title: string) => void;
}) {
  const posthog = usePostHog();
  // The same event as a pinned chat's click; the title stays out.
  const track = () =>
    posthog?.capture('sidebar_nav_clicked', {
      placement: 'recent_chat',
      kind: tab.agentId ? 'agent_chat' : 'chat',
      page: undefined,
      locked: false,
    });
  const pin = useMemo(() => chatItem(tab), [tab]);
  const { editing, start, done } = useRenameInPlace({
    id: tab.pending ? null : tab.id,
    rename: onRename,
    focusRow: () => focusFirst([pin.key], document),
  });
  const { nodeRef, handleProps, isDragging } = useSortablePin(
    'tabs',
    pin,
    'row',
    tab.pending || editing
  );
  const className = tabClasses(active);
  const content = (
    <>
      <ChatTabIcon tab={tab} />
      <span className={cn('min-w-0 flex-1 truncate', itemLabel(active))}>
        {tab.title}
      </span>
      {running && (
        <span role='img' aria-label='Running' className='shrink-0'>
          <AppIcon
            icon={iconSpinnerThird}
            className='animate-spin text-primary'
            style={{ fontSize: 13 }}
          />
        </span>
      )}
    </>
  );
  const item = editing ? (
    <div
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-[10px] pr-1.5 pl-2 text-foreground',
        active && RAISED_CARD
      )}
    >
      <ChatTabIcon tab={tab} />
      <ChatTitleField
        title={tab.title}
        onDone={done}
        className={cn('h-7', active && 'font-semibold')}
      />
    </div>
  ) : tab.agentId ? (
    <Link
      to='/agents/$agentId'
      params={{ agentId: tab.agentId }}
      search={tab.pending ? {} : { chat: tab.id }}
      {...handleProps}
      data-pin-key={pin.key}
      onClick={track}
      aria-current={active ? 'page' : undefined}
      className={className}
    >
      {content}
    </Link>
  ) : (
    <button
      type='button'
      {...handleProps}
      data-pin-key={pin.key}
      onClick={() => {
        track();
        onOpenClu(tab.id);
      }}
      aria-current={active ? 'page' : undefined}
      className={className}
    >
      {content}
    </button>
  );
  if (tab.pending)
    return (
      <div
        ref={nodeRef}
        className='relative grid min-w-0 [grid-template-columns:minmax(0,1fr)]'
      >
        {item}
      </div>
    );
  return (
    <SidebarItemMenu
      ref={nodeRef}
      sidebarRef={pin.ref}
      label={tab.title}
      place={CHAT_TAB}
      button
      onRename={start}
      editing={editing}
      className={cn('grid', isDragging && 'opacity-40')}
    >
      {item}
    </SidebarItemMenu>
  );
}
